import React, { useState, useRef, useEffect } from 'react';
import { Layout, Input, Button, List, Upload, Modal, Tooltip } from 'antd';
import {
  UploadOutlined,
  SendOutlined,
  CloudUploadOutlined,
  ClearOutlined,
  LoadingOutlined,
} from '@ant-design/icons';

const { Content, Footer } = Layout;
const { TextArea } = Input;

const ChatWindow = (props) => {
  const { clearChat } = props;
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [disableFileUpload, setDisableFileUpload] = useState(false);
  const [bulkUploadModal, setBulkUploadModal] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);

  const messagesEndRef = useRef(null);
  const llm_url = process.env.REACT_APP_LMM_ASSISTANCE;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    setMessages([]);
  }, [clearChat]);
  const handleSend = async () => {
    if (!inputValue.trim()) return;
    setSendLoading(true);
    const userMsg = { text: inputValue, sender: 'user' };
    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');
    setLoading(true);

    try {
      const res = await fetch(`${llm_url}/chat`, {
        method: 'POST',
        headers: {
          Authorization: 'Bearer test-token',
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({ question: inputValue }),
      });

      const data = await res.json();
      const botMsg = { text: data.answer || 'No response', sender: 'bot' };
      setMessages((prev) => [...prev, botMsg]);
      setSendLoading(true);
    } catch (err) {
      setMessages((prev) => [...prev, { text: 'Error contacting assistant.', sender: 'bot' }]);
    } finally {
      setSendLoading(false);
    }

    setLoading(false);
  };

  const handleClear = async () => {
    setMessages([]);
  };

  const handleFileUpload = async (file) => {
    setLoading(true);
    const fileUrl = URL.createObjectURL(file);
    setMessages((prev) => [...prev, { text: file.name, fileUrl, sender: 'user' }]);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${llm_url}/upload`, {
        method: 'POST',
        headers: {
          Authorization: 'Bearer test-token',
          // Note: Do NOT set 'Content-Type' header here
        },
        body: formData,
      });

      const data = await res.json();
      console.log(data);
      const botMsg = { text: data.status || 'No response', sender: 'bot' };
      setMessages((prev) => [...prev, botMsg]);
    } catch (error) {
      console.error('Upload failed:', error);
      const botMsg = { text: 'Upload failed. Please try again.', sender: 'bot' };
      setMessages((prev) => [...prev, botMsg]);
    } finally {
      setLoading(false);
    }

    return false; // prevent auto-upload
  };

  const fileProps = {
    beforeUpload: (file) => {
      const newList = [...fileList, file];
      setFileList(newList);
      setDisableFileUpload(newList.length >= 5);
      return false;
    },
    onRemove: (file) => {
      const newFileList = fileList.filter((f) => f.uid !== file.uid);
      setFileList(newFileList);
      setDisableFileUpload(false);
    },
    fileList,
  };

  const handleUploadFile = async () => {
    setUploadLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    const res = await fetch(`${llm_url}/chat`, {
      method: 'POST',
      headers: {
        Authorization: 'Bearer test-token',
      },
      body: formData,
    });

    const data = await res.json();
    //setStatus(`Upload status: ${data.status}`);
    // Simulate file upload
    setTimeout(() => {
      setUploadLoading(false);
      setBulkUploadModal(false);
      setMessages((prev) => [
        ...prev,
        { text: fileList[0].name, sender: 'user', fileUrl: URL.createObjectURL(fileList[0]) },
      ]);
      setFileList([]);
      setDisableFileUpload(false);
    }, 1500);
  };
  // Auto-resize function
  const autoResizeTextarea = (textarea) => {
    textarea.style.height = 'auto'; // Reset height
    textarea.style.height = `${textarea.scrollHeight}px`; // Set to content height
  };

  return (
    <div className="assistance-layout">
      <Layout
        style={{ height: 'calc(100vh - 100px)' }}
        className="pipeline-modal"
      >
        <Content style={{ padding: 16, overflowY: 'auto' }}>
          {messages.length > 0 ? (
            <List
              dataSource={messages}
              renderItem={(item, i) => (
                <List.Item
                  key={i}
                  style={{
                    justifyContent: item.sender === 'user' ? 'flex-end' : 'flex-start',
                  }}
                >
                  <div
                    style={{
                      maxWidth: '70%',
                      textAlign: item.sender === 'user' ? 'right' : 'left',
                    }}
                  >
                    {item.fileUrl ? (
                      <a
                        href={item.fileUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <div className={`chat-message ${item.sender}`}>{item.text}</div>
                      </a>
                    ) : (
                      <div className={`chat-message ${item.sender}`}>{item.text}</div>
                    )}
                  </div>
                </List.Item>
              )}
            />
          ) : (
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding: 24,
                color: '#888',
              }}
            >
              <div style={{ fontSize: '18px' }}>Ask Evans</div>
              <p>Your AI Assistant, Powered by CRCE Connect</p>
              <p>Ask Evans is an AI-powered, proprietary chatbot designed by CRC Evans.</p>
              <p>
                It helps you quickly find answers to questions about machine operations, setup
                instructions, and more, making your work easier and faster.
              </p>
            </div>
          )}

          {loading && <div style={{ fontStyle: 'italic', padding: 8 }}>Thinking...</div>}
          <div ref={messagesEndRef} />
        </Content>

        <Footer style={{ padding: 16 }}>
          <div className="chat-input-area">
            <div className="chat-input-wrapper">
              <div className="textarea-container">
                <Input.TextArea
                  className="chat-textarea"
                  value={inputValue}
                  onChange={(e) => {
                    setInputValue(e.target.value);
                    // autoResizeTextarea(e.target);
                  }}
                  autoSize={{ minRows: 1, maxRows: 6 }}
                  size="large"
                  placeholder="Type your message..."
                  onPressEnter={(e) => {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  style={{
                    resize: 'none',
                    lineHeight: '1.5',
                    borderRadius: '10px',
                    padding: '10px 16px',
                    paddingRight: '80px', // Make space for buttons
                  }}
                />
              </div>
              <div className="textarea-buttons">
                <Tooltip
                  title="Send"
                  style={{ position: 'relative', marginLeft: '0px' }}
                >
                  <Button
                    type="text"
                    // icon={}
                    onClick={handleSend}

                    //disabled={loading || !inputValue.trim()}
                    // className="action-button"
                  >
                    {' '}
                    {sendLoading ? (
                      <LoadingOutlined
                        className="icon-22"
                        style={{ fontSize: '22px', color: '#EF314C' }}
                      ></LoadingOutlined>
                    ) : (
                      <SendOutlined
                        className="icon-22"
                        style={{ fontSize: '22px', color: '#EF314C' }}
                      />
                    )}
                  </Button>
                </Tooltip>
                {/* <Tooltip title="Clear">
                    <Button
                      type="primary"
                    // icon={<ClearOutlined />}
                      onClick={handleClear}
                      disabled={loading}
                  //className="action-button"

                  ><ClearOutlined style={{ fontSize: '22px' }} /></Button>
                  </Tooltip> */}
              </div>
            </div>
          </div>
          <p
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              paddingTop: '10px',
              color: '#888',
            }}
          >
            AI can make mistakes. Check important info..
          </p>
        </Footer>

        {/* <Modal
          className="pipeline-modal"
          title="Upload DCAL Details"
          centered
          open={bulkUploadModal}
          onCancel={() => setBulkUploadModal(false)}
          footer={[
            <Button key="cancel" onClick={() => setBulkUploadModal(false)}>
              Cancel
            </Button>,
            <Button key="upload" type="primary" onClick={handleUploadFile} loading={uploadLoading}>
              Upload
            </Button>,
          ]}
        >
          <div className="uploadFile_admin">
            <Upload {...fileProps} accept=".xlsx,.xls,.pdf" multiple maxCount={5}>
              <Button disabled={disableFileUpload} icon={<CloudUploadOutlined />}>
                Select Files
              </Button>
            </Upload>
          </div>
        </Modal> */}
      </Layout>
    </div>
  );
};

export default ChatWindow;
